import { Fragment, FC, VFC, useState, useRef, useEffect, useMemo } from 'react';
import { Box, Pagination, PaginationItem } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {
  H2,
  StyledPageContainer,
  StyledPageContent,
  StyledPageWrapper,
} from '../../components/styles';
import api from '../../config/api';
import { Hero } from '../../components/Hero/Hero';
import { SearchArea } from './SearchArea/SearchArea';
import { VotesList } from '../../components/VotesList/VotesList';
import { IVoteBoxProps } from '../../components/VotesList/types';
import { VoteResultSelectValues } from './SearchArea/types';
import styled from '@emotion/styled';
import { breakpoints } from '../../styles/variables/breakpoints';
import { Link, LinkProps, useLocation, useNavigate } from 'react-router-dom';

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

export const StyledHeroCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 60px;
  align-items: center;

  @media ${breakpoints.width992} {
    grid-template-columns: 1fr;
  }
`;

export const StyledHeroCol = styled.div`
  display: block;
`;

export interface ISearchOptions {
  query: string;
  year: number;
  result: VoteResultSelectValues;
}

export const StyledLink: FC<LinkProps> = ({ children, style, ...props }) => (
  <Link {...props} style={{ ...style }}>
    {children}
  </Link>
);

export const Home: VFC = () => {
  const [searchOptions, setSearchOptions] = useState<ISearchOptions>({
    query: '',
    result: VoteResultSelectValues.All,
    year: 0,
  });
  const [results, setResults] = useState<IVoteBoxProps[] | undefined>(
    undefined
  );
  const [paginationPageCount, setPaginationPageCount] = useState<number>();
  const [paginationPage, setPaginationPage] = useState<number>(1);

  const resultsEl = useRef<HTMLInputElement>(null);

  const getNewResults = (options: ISearchOptions, pageNumber: number) => {
    let yearFilter = '';
    let voteResultFilter = '';

    if (options.year) {
      const endYear = options.year + 1;

      yearFilter = `&filters[$and][0][datetime][$gte]=${options.year}-01-01&filters[$and][1][datetime][$lt]=${endYear}-01-01`;
    }

    if (options.result && options.result !== VoteResultSelectValues.All) {
      voteResultFilter = `&filters[result][$eq]=${options.result}`;
    }

    api
      .get(
        `voting-records?filters[subject][$containsi]=${options.query}${yearFilter}${voteResultFilter}&sort=datetime:desc&pagination[pageSize]=100&pagination[page]=${pageNumber}`
      )
      .then((response) => {
        setPaginationPageCount(response.data.meta.pagination.pageCount);
        const data = response.data.data;

        const formattedData = data.map((dataResult: any) => {
          const resultFormatted: IVoteBoxProps = {
            subject: dataResult.attributes.subject,
            date: dataResult.attributes.datetime,
            result: dataResult.attributes.result,
            meetingCode: dataResult.attributes.code,
            id: dataResult.id,
          };

          return resultFormatted;
        });

        setResults(formattedData);
      });
  };

  const navigate = useNavigate();
  const goToSearchPage = (page: number) => {
    navigate(
      `?q=${searchOptions.query}&y=${searchOptions.year}&r=${searchOptions.result}&p=${page}`
    );
  };
  const onSubmit = () => {
    goToSearchPage(1);
  };

  const handlePaginationChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    goToSearchPage(value);
    const resultsY =
      (resultsEl?.current?.getBoundingClientRect().top ?? -window.scrollY) +
      window.scrollY -
      110;
    window.scrollTo(0, Math.max(resultsY, 0));
  };

  const queryParams = useQuery();
  const queryQuery = queryParams.get('q');
  const queryYear = queryParams.get('y') ?? '';
  const queryResult = queryParams.get('r');
  const queryPage = queryParams.get('p') ?? '';
  useEffect(() => {
    const parsedYear = parseInt(queryYear);
    const parsedPage = parseInt(queryPage);

    const newOptions = {
      ...searchOptions,
      query: queryQuery ?? '',
      year: isNaN(parsedYear) ? 0 : parsedYear,
      result:
        queryResult !== null
          ? (queryResult as VoteResultSelectValues)
          : VoteResultSelectValues.All,
    };
    setSearchOptions(newOptions);

    if (queryQuery !== null) {
      const newPage = isNaN(parsedPage) ? 1 : parsedPage;

      setPaginationPage(newPage);
      getNewResults(newOptions, newPage);
    } else {
      setResults(undefined);
    }
  }, [queryQuery, queryYear, queryResult, queryPage]);

  const renderVotesList = () => {
    if (results === undefined) {
      return null;
    }

    if (results.length === 0) {
      return (
        <Fragment>
          Nebyly nalezeny žádné výsledky, zkuste opakovat vyhledávání s jinými
          hodnotami.
        </Fragment>
      );
    }

    return (
      <div ref={resultsEl}>
        <VotesList votes={results} />
        <Box marginTop={'16px'}>
          <Pagination
            sx={{ display: 'flex', justifyContent: 'center' }}
            count={paginationPageCount}
            size={window.innerWidth < 410 ? 'small' : 'medium'}
            page={paginationPage}
            onChange={handlePaginationChange}
            renderItem={(item) => (
              <PaginationItem
                components={{
                  previous: ArrowBackIcon,
                  next: ArrowForwardIcon,
                }}
                {...item}
              />
            )}
          />
        </Box>
      </div>
    );
  };

  return (
    <Fragment>
      <StyledPageWrapper>
        <Hero>
          <StyledPageContainer>
            <H2>Hlasování brněnského zastupitelstva</H2>
            <StyledHeroCols>
              <StyledHeroCol>
                <SearchArea
                  onSubmit={onSubmit}
                  options={searchOptions}
                  setOptions={setSearchOptions}
                />
              </StyledHeroCol>
              <StyledHeroCol>
                <p>
                  <b>Jak vyhledávat?</b>
                </p>
                <p>
                  Pro vyhledání konkrétního hlasování použijte vyhledávací pole.
                  Jednotlivá klíčová slova oddělujte mezerou. Vyhledávač pracuje
                  nad výsledky všech bodů hlasování brněnského zastupitelstva od
                  roku 2018.
                </p>
                <p>
                  <StyledLink to="/about">více informací</StyledLink>
                </p>
              </StyledHeroCol>
            </StyledHeroCols>
          </StyledPageContainer>
        </Hero>
        <StyledPageContainer>
          <StyledPageContent>{renderVotesList()}</StyledPageContent>
        </StyledPageContainer>
      </StyledPageWrapper>
    </Fragment>
  );
};
